import React, { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import api from "../../lib/api";
import AdminLayout from "../../components/AdminLayout";
import { toast } from "sonner";
import { CATEGORIES } from "../../lib/utils-app";
import {
  AlertTriangle, Check, X, Send, Save, ChevronLeft, Plus, Sparkles,
  Calendar, Link2, ShieldCheck, ExternalLink, Loader2,
} from "lucide-react";

const Field = ({ label, children }) => (
  <div>
    <label className="kicker">{label}</label>
    <div className="mt-1.5">{children}</div>
  </div>
);

const verdictColor = (v) =>
  v === "high" ? "text-[#EF4444]" : v === "medium" ? "text-[#F59E0B]" : "text-[#10B981]";

export default function DraftEditor() {
  const { id } = useParams();
  const nav = useNavigate();
  const [d, setD] = useState(null);
  const [saving, setSaving] = useState(false);
  const [scheduling, setScheduling] = useState(false);
  const [scheduledFor, setScheduledFor] = useState("");
  const [showSchedule, setShowSchedule] = useState(false);

  const [related, setRelated] = useState(null);
  const [loadingRelated, setLoadingRelated] = useState(false);

  const [plagiarism, setPlagiarism] = useState(null);
  const [loadingPlag, setLoadingPlag] = useState(false);

  useEffect(() => {
    document.title = "Edit draft — IndiaODR Admin";
    api.get(`/drafts/${id}`).then((r) => setD(r.data));
    // Auto-fetch related-links suggestions on draft open
    setLoadingRelated(true);
    api
      .get(`/admin/drafts/${id}/related-links?limit=8`)
      .then((r) => setRelated(r.data?.suggestions || []))
      .catch(() => setRelated([]))
      .finally(() => setLoadingRelated(false));
  }, [id]);

  if (!d) return <AdminLayout><div className="text-[var(--io-muted)]">Loading draft…</div></AdminLayout>;

  const update = (patch) => setD({ ...d, ...patch });

  const save = async (extra = {}) => {
    setSaving(true);
    try {
      const body = {
        headline: d.headline, slug: d.slug, summary: d.summary,
        what_happened: d.what_happened, key_legal_issue: d.key_legal_issue,
        why_it_matters: d.why_it_matters, timeline: d.timeline,
        business_impact: d.business_impact, india_odr_relevance: d.india_odr_relevance,
        meta_title: d.meta_title, meta_description: d.meta_description,
        tags: d.tags, category: d.category, source_refs: d.source_refs, faq: d.faq,
        ...extra,
      };
      const { data } = await api.patch(`/drafts/${id}`, body);
      setD(data); toast.success("Saved.");
    } catch { toast.error("Save failed."); }
    finally { setSaving(false); }
  };

  const approve = () => save({ status: "approved" }).then(() => toast.success("Draft approved."));
  const reject = async () => {
    if (!confirm("Reject this draft?")) return;
    await api.post(`/drafts/${id}/reject`);
    toast.success("Rejected.");
    nav("/admin/drafts");
  };

  const doPublish = async (force = false) => {
    try {
      const url = force ? `/drafts/${id}/publish?force=true` : `/drafts/${id}/publish`;
      const { data } = await api.post(url);
      toast.success(force ? "Published (override)." : "Published.");
      nav(`/admin/drafts`);
      window.open(`/article/${data.slug}`, "_blank");
    } catch (e) {
      const detail = e?.response?.data?.detail;
      // Plagiarism gate (HTTP 409)
      if (detail && typeof detail === "object" && detail.code === "plagiarism_high") {
        if (
          confirm(
            `Plagiarism similarity is HIGH (score: ${detail.report?.score}).\n\n` +
              `Top match: ${detail.report?.matches?.[0]?.source_name || "—"}\n\n` +
              `Override and publish anyway?`
          )
        ) {
          return doPublish(true);
        }
        return;
      }
      toast.error(typeof detail === "string" ? detail : "Publish failed.");
    }
  };

  const publish = () => {
    if (!confirm(`Publish "${d.headline}"? This will make it live.`)) return;
    return doPublish(false);
  };

  const schedule = async () => {
    if (!scheduledFor) {
      toast.error("Pick a date and time.");
      return;
    }
    setScheduling(true);
    try {
      const iso = new Date(scheduledFor).toISOString();
      const { data } = await api.post(
        `/drafts/${id}/schedule?scheduled_for=${encodeURIComponent(iso)}`
      );
      setD(data);
      setShowSchedule(false);
      toast.success(`Scheduled for ${new Date(iso).toLocaleString()}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Schedule failed.");
    } finally {
      setScheduling(false);
    }
  };

  const runPlagiarism = async () => {
    setLoadingPlag(true);
    try {
      const { data } = await api.get(`/admin/drafts/${id}/plagiarism-check`);
      setPlagiarism(data);
      // Refresh draft to pick up new plagiarism_risk
      const { data: draft } = await api.get(`/drafts/${id}`);
      setD(draft);
      toast.success(`Plagiarism check: ${data.verdict.toUpperCase()} (${data.score})`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Plagiarism check failed.");
    } finally {
      setLoadingPlag(false);
    }
  };

  const isSensitive = (d.sensitive_flags || []).length > 0 || d.legal_risk === "high";
  const input =
    "w-full border border-[var(--io-border)] focus:border-[var(--io-gold)] bg-[var(--io-bg-2)] px-3 py-2 rounded-md text-[14px] text-[var(--io-text)] outline-none";

  return (
    <AdminLayout>
      <div className="flex items-start justify-between mb-6 flex-wrap gap-3">
        <div>
          <Link to="/admin/drafts" className="inline-flex items-center gap-1 text-[11px] font-mono text-[var(--io-text-2)] hover:text-[var(--io-gold)]" data-testid="back-to-queue">
            <ChevronLeft size={14}/> Back to queue
          </Link>
          <h1 className="font-serif text-[28px] md:text-[36px] mt-2 text-[var(--io-text)]">Draft editor</h1>
          <div className="mt-2 flex items-center gap-2 text-[12px] font-mono">
            <span className="io-badge" data-testid="draft-status">{d.status}</span>
            <span className={d.ai_confidence >= 0.9 ? "text-[#10B981]" : d.ai_confidence >= 0.7 ? "text-[var(--io-gold)]" : "text-[#F59E0B]"}>
              AI {Math.round((d.ai_confidence || 0) * 100)}%
            </span>
            {d.scheduled_for && (
              <span className="text-[var(--io-gold)] flex items-center gap-1" data-testid="scheduled-for-label">
                <Calendar size={12}/> {new Date(d.scheduled_for).toLocaleString()}
              </span>
            )}
          </div>
        </div>
        <div className="flex flex-wrap gap-2" data-testid="draft-actions">
          <button onClick={() => save()} disabled={saving} className="btn btn-ghost !text-[12px] !py-2" data-testid="draft-save"><Save size={14}/> Save</button>
          <button onClick={approve} className="btn !text-[12px] !py-2" style={{ background: "#10B981", color: "#063" }} data-testid="draft-approve"><Check size={14}/> Approve</button>
          <button onClick={() => setShowSchedule((v) => !v)} className="btn btn-ghost !text-[12px] !py-2" data-testid="draft-schedule-toggle"><Calendar size={14}/> Schedule</button>
          <button onClick={reject} className="btn btn-ghost !text-[12px] !py-2 !text-[#EF4444]" data-testid="draft-reject"><X size={14}/> Reject</button>
          <button onClick={publish} className="btn btn-primary !text-[12px] !py-2" data-testid="draft-publish"><Send size={14}/> Publish</button>
        </div>
      </div>

      {showSchedule && (
        <div className="io-card-el p-4 mb-6 flex flex-wrap items-end gap-3" data-testid="schedule-dialog">
          <div className="flex-1 min-w-[260px]">
            <label className="kicker">Schedule for (your local time)</label>
            <input
              type="datetime-local"
              value={scheduledFor}
              onChange={(e) => setScheduledFor(e.target.value)}
              className={`${input} mt-1.5`}
              data-testid="schedule-datetime"
            />
            <div className="text-[10px] font-mono text-[var(--io-muted)] mt-1">
              The scheduled-publish job runs every 5 minutes; high-plagiarism drafts will be kicked back to review instead of auto-publishing.
            </div>
          </div>
          <button onClick={schedule} disabled={scheduling || !scheduledFor} className="btn btn-primary !text-[12px] !py-2" data-testid="schedule-confirm">
            {scheduling ? <Loader2 size={14} className="animate-spin"/> : <Calendar size={14}/>} Confirm schedule
          </button>
          <button onClick={() => setShowSchedule(false)} className="btn btn-ghost !text-[12px] !py-2" data-testid="schedule-cancel"><X size={14}/> Cancel</button>
        </div>
      )}

      {isSensitive && (
        <div className="io-card-el p-4 mb-6 border-l-2 border-[#EF4444] flex items-start gap-3" data-testid="sensitive-warning">
          <AlertTriangle className="text-[#EF4444] shrink-0 mt-0.5" size={20}/>
          <div>
            <div className="font-sans font-semibold text-[var(--io-text)]">Sensitive content — manual review required</div>
            <div className="text-[13px] text-[var(--io-text-2)] mt-1">
              Legal risk: <span className="text-[#EF4444] font-mono">{d.legal_risk}</span> · Flags: <span className="font-mono text-[#F59E0B]">{(d.sensitive_flags || []).join(", ") || "none"}</span>.
              Confirm attribution, neutral tone ("alleged", "reportedly"), and that no unverified claims about named individuals remain.
            </div>
          </div>
        </div>
      )}

      <div className="grid lg:grid-cols-3 gap-5">
        <div className="lg:col-span-2 space-y-5">
          <div className="io-card-el p-5 space-y-4">
            <Field label="Headline">
              <input value={d.headline} onChange={(e) => update({ headline: e.target.value })} className={input} data-testid="edit-headline"/>
            </Field>
            <Field label="Slug">
              <input value={d.slug} onChange={(e) => update({ slug: e.target.value })} className={`${input} font-mono text-[12px]`} data-testid="edit-slug"/>
            </Field>
            <Field label="Summary (~80 words)">
              <textarea rows={3} value={d.summary || ""} onChange={(e) => update({ summary: e.target.value })} className={input} data-testid="edit-summary"/>
            </Field>
            <Field label="Category">
              <select value={d.category} onChange={(e) => update({ category: e.target.value })} className={input} data-testid="edit-category">
                {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
            </Field>
          </div>

          {[
            ["what_happened", "What happened"],
            ["key_legal_issue", "Key legal issue"],
            ["why_it_matters", "Why it matters"],
            ["timeline", "Timeline"],
            ["business_impact", "Business impact"],
            ["india_odr_relevance", "India & ODR relevance"],
          ].map(([k, label]) => (
            <div key={k} className="io-card-el p-5">
              <Field label={label}>
                <textarea rows={k === "timeline" ? 6 : 8} value={d[k] || ""} onChange={(e) => update({ [k]: e.target.value })} className={input} data-testid={`edit-${k}`}/>
              </Field>
            </div>
          ))}
        </div>

        <aside className="space-y-5">
          {/* Editorial signals */}
          <div className="io-card-el p-5">
            <div className="flex items-center justify-between mb-4">
              <div className="eyebrow">Editorial signals</div>
              <Sparkles size={14} className="text-[var(--io-gold)]"/>
            </div>
            <dl className="space-y-2.5 text-[12px] font-mono">
              <div className="flex justify-between"><dt className="text-[var(--io-muted)]">AI confidence</dt><dd className={d.ai_confidence >= 0.9 ? "text-[#10B981]" : d.ai_confidence >= 0.7 ? "text-[var(--io-gold)]" : "text-[#F59E0B]"}>{Math.round((d.ai_confidence || 0) * 100)}%</dd></div>
              <div className="flex justify-between"><dt className="text-[var(--io-muted)]">Legal risk</dt><dd className={verdictColor(d.legal_risk)}>{d.legal_risk}</dd></div>
              <div className="flex justify-between"><dt className="text-[var(--io-muted)]">Plagiarism risk</dt><dd className={verdictColor(d.plagiarism_risk)} data-testid="plagiarism-risk">{d.plagiarism_risk}</dd></div>
              <div>
                <div className="text-[var(--io-muted)] mb-1">Flags</div>
                <div className="flex flex-wrap gap-1">
                  {(d.sensitive_flags || []).length === 0 ? <span className="text-[#10B981]">none</span> : d.sensitive_flags.map((f) => <span key={f} className="io-badge io-badge-amber">{f}</span>)}
                </div>
              </div>
            </dl>
          </div>

          {/* Plagiarism panel */}
          <div className="io-card-el p-5" data-testid="plagiarism-panel">
            <div className="flex items-center justify-between mb-3">
              <div className="eyebrow flex items-center gap-1.5"><ShieldCheck size={13}/> Plagiarism check</div>
              <button onClick={runPlagiarism} disabled={loadingPlag} className="text-[11px] font-mono text-[var(--io-gold)] hover:text-[var(--io-text)] flex items-center gap-1" data-testid="run-plagiarism">
                {loadingPlag ? <Loader2 size={12} className="animate-spin"/> : null} Run
              </button>
            </div>
            {plagiarism ? (
              <div className="text-[12px] font-mono space-y-2">
                <div className="flex justify-between">
                  <span className="text-[var(--io-muted)]">Verdict</span>
                  <span className={verdictColor(plagiarism.verdict)} data-testid="plagiarism-verdict">{plagiarism.verdict.toUpperCase()}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-[var(--io-muted)]">Score</span>
                  <span className="text-[var(--io-text)]">{plagiarism.score}</span>
                </div>
                {(plagiarism.matches || []).length > 0 ? (
                  <div className="pt-2 border-t border-[var(--io-border)]">
                    <div className="text-[var(--io-muted)] mb-1.5">Top matches</div>
                    <ul className="space-y-1.5">
                      {plagiarism.matches.slice(0, 3).map((m, i) => (
                        <li key={i} className="text-[11px]">
                          <div className="text-[var(--io-text)] truncate">{m.source_name || "source"} · <span className={verdictColor(plagiarism.verdict)}>{m.similarity}</span></div>
                          {m.url && <a href={m.url} target="_blank" rel="noreferrer" className="text-[#3B82F6] hover:underline truncate inline-flex items-center gap-1"><ExternalLink size={10}/> {m.url}</a>}
                        </li>
                      ))}
                    </ul>
                  </div>
                ) : (
                  <div className="text-[11px] text-[var(--io-muted)]">{plagiarism.note || "No matches above threshold."}</div>
                )}
              </div>
            ) : (
              <div className="text-[11px] text-[var(--io-muted)]">Click <span className="text-[var(--io-gold)]">Run</span> to compare this draft against ingested source snippets.</div>
            )}
          </div>

          {/* Related links suggestions */}
          <div className="io-card-el p-5" data-testid="related-links-panel">
            <div className="eyebrow flex items-center gap-1.5 mb-3"><Link2 size={13}/> Related links</div>
            {loadingRelated ? (
              <div className="text-[11px] text-[var(--io-muted)] flex items-center gap-1.5"><Loader2 size={12} className="animate-spin"/> Loading suggestions…</div>
            ) : related && related.length === 0 ? (
              <div className="text-[11px] text-[var(--io-muted)]">No internal-link suggestions yet.</div>
            ) : (
              <ul className="space-y-2.5" data-testid="related-links-list">
                {(related || []).map((r) => (
                  <li key={r.slug} className="text-[12px] border-b border-[var(--io-border)] last:border-0 pb-2 last:pb-0">
                    <a href={r.link} target="_blank" rel="noreferrer" className="text-[var(--io-text)] hover:text-[var(--io-gold)] line-clamp-2">
                      {r.headline}
                    </a>
                    <div className="font-mono text-[10px] text-[var(--io-muted)] mt-0.5 flex items-center gap-2">
                      <span>{r.category}</span>
                      <span>·</span>
                      <span className="text-[var(--io-gold)]">sim {r.similarity}</span>
                    </div>
                  </li>
                ))}
              </ul>
            )}
            <div className="text-[10px] font-mono text-[var(--io-muted)] mt-3 pt-2 border-t border-[var(--io-border)]">
              Top 3 suggestions are auto-attached to the article on publish.
            </div>
          </div>

          {/* SEO */}
          <div className="io-card-el p-5">
            <div className="eyebrow mb-3">SEO</div>
            <Field label="Meta title">
              <input value={d.meta_title || ""} onChange={(e) => update({ meta_title: e.target.value })} className={input} data-testid="edit-meta-title"/>
              <div className="text-[10px] font-mono text-[var(--io-muted)] mt-1">{(d.meta_title || "").length}/60</div>
            </Field>
            <div className="mt-3"><Field label="Meta description">
              <textarea rows={3} value={d.meta_description || ""} onChange={(e) => update({ meta_description: e.target.value })} className={input} data-testid="edit-meta-desc"/>
              <div className="text-[10px] font-mono text-[var(--io-muted)] mt-1">{(d.meta_description || "").length}/155</div>
            </Field></div>
            <div className="mt-3"><Field label="Tags (comma-separated)">
              <input value={(d.tags || []).join(", ")} onChange={(e) => update({ tags: e.target.value.split(",").map((s) => s.trim()).filter(Boolean) })} className={input} data-testid="edit-tags"/>
            </Field></div>
          </div>

          {/* Sources */}
          <div className="io-card-el p-5">
            <div className="eyebrow mb-3">Sources</div>
            <div className="space-y-2 text-[13px]">
              {(d.source_refs || []).map((s, i) => (
                <div key={i} className="border border-[var(--io-border)] rounded-md p-2.5 bg-[var(--io-bg-2)]">
                  <input placeholder="Publisher name" value={s.name || ""} onChange={(e) => { const n = [...d.source_refs]; n[i] = { ...n[i], name: e.target.value }; update({ source_refs: n }); }}
                    className="w-full bg-transparent text-[var(--io-text)] outline-none text-[13px]" data-testid={`src-name-${i}`}/>
                  <input placeholder="URL" value={s.url || ""} onChange={(e) => { const n = [...d.source_refs]; n[i] = { ...n[i], url: e.target.value }; update({ source_refs: n }); }}
                    className="w-full bg-transparent text-[11px] outline-none font-mono text-[#3B82F6] mt-1" data-testid={`src-url-${i}`}/>
                </div>
              ))}
              <button onClick={() => update({ source_refs: [...(d.source_refs || []), { name: "", url: "" }] })} className="text-[11px] font-mono text-[var(--io-gold)] hover:text-[var(--io-text)] flex items-center gap-1" data-testid="add-source-ref"><Plus size={12}/> Add source</button>
            </div>
          </div>

          <div className="io-card-el p-5 text-[11px] font-mono text-[var(--io-muted)]">
            <div className="eyebrow mb-2">Disclaimer (auto)</div>
            <div>{d.disclaimer}</div>
          </div>
        </aside>
      </div>
    </AdminLayout>
  );
}
